import React from "react";

// Renders the parsed strategy JSON. Tolerant of missing sections and of
// string-vs-object shapes, since the AI output can vary slightly.
const asText = (v) => (typeof v === "string" ? v : v == null ? "" : JSON.stringify(v));

function Section({ title, children }) {
  if (children == null || (Array.isArray(children) && children.length === 0)) return null;
  return (
    <div className="strat-section">
      <h4>{title}</h4>
      {children}
    </div>
  );
}

function List({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <ul className="strat-list">
      {items.map((it, i) => (
        <li key={i}>{typeof it === "string" ? it : asText(it)}</li>
      ))}
    </ul>
  );
}

export default function StrategyViewer({ strategy }) {
  if (!strategy) return null;
  const s = strategy;
  const aud = s.targetAudience || {};
  const funnel = s.marketingFunnel || {};
  const budget = s.budgetAllocation || {};

  return (
    <div className="strat">
      <Section title="Executive Summary">
        <p>{asText(s.executiveSummary)}</p>
      </Section>

      {s.businessAnalysis && (
        <Section title="Business Analysis">
          <p>{asText(s.businessAnalysis)}</p>
        </Section>
      )}

      {(aud.primary || aud.secondary || aud.demographics || aud.painPoints) && (
        <Section title="Target Audience">
          {aud.primary && <p><strong>Primary:</strong> {asText(aud.primary)}</p>}
          {aud.secondary && <p><strong>Secondary:</strong> {asText(aud.secondary)}</p>}
          {aud.demographics && <p><strong>Demographics:</strong> {asText(aud.demographics)}</p>}
          {aud.painPoints && <p><strong>Pain points:</strong> {asText(aud.painPoints)}</p>}
        </Section>
      )}

      {(funnel.awareness || funnel.consideration || funnel.conversion || funnel.retention) && (
        <Section title="Marketing Funnel">
          {["awareness", "consideration", "conversion", "retention"].map(
            (k) => funnel[k] && <p key={k}><strong>{k}:</strong> {asText(funnel[k])}</p>
          )}
        </Section>
      )}

      {s.googleAdsStrategy && (
        <Section title="Google Ads Strategy">
          <p>{asText(s.googleAdsStrategy)}</p>
        </Section>
      )}
      {s.metaAdsStrategy && (
        <Section title="Meta Ads Strategy">
          <p>{asText(s.metaAdsStrategy)}</p>
        </Section>
      )}

      {(budget.summary || Array.isArray(budget.breakdown)) && (
        <Section title="Budget Allocation">
          {budget.summary && <p>{asText(budget.summary)}</p>}
          {Array.isArray(budget.breakdown) && budget.breakdown.length > 0 && (
            <table className="strat-table">
              <thead>
                <tr><th>Channel</th><th>%</th><th>Amount</th><th>Rationale</th></tr>
              </thead>
              <tbody>
                {budget.breakdown.map((b, i) => (
                  <tr key={i}>
                    <td>{asText(b.channel)}</td>
                    <td>{b.percentage != null ? `${b.percentage}%` : "—"}</td>
                    <td>{b.amount != null ? b.amount : "—"}</td>
                    <td>{asText(b.rationale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      )}

      {s.creativeRecommendations && (
        <Section title="Creative Recommendations">
          <List items={s.creativeRecommendations} />
        </Section>
      )}
      {s.landingPageSuggestions && (
        <Section title="Landing Page Suggestions">
          <List items={s.landingPageSuggestions} />
        </Section>
      )}
      {s.trackingPlan && (
        <Section title="Tracking Plan">
          <p>{asText(s.trackingPlan)}</p>
        </Section>
      )}

      {Array.isArray(s.kpiForecast) && s.kpiForecast.length > 0 && (
        <Section title="KPI Forecast">
          <table className="strat-table">
            <thead><tr><th>Metric</th><th>Target</th><th>Estimate</th></tr></thead>
            <tbody>
              {s.kpiForecast.map((k, i) => (
                <tr key={i}>
                  <td>{asText(k.metric)}</td>
                  <td>{asText(k.target)}</td>
                  <td>{asText(k.estimate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {Array.isArray(s.riskAnalysis) && s.riskAnalysis.length > 0 && (
        <Section title="Risk Analysis">
          <ul className="strat-list">
            {s.riskAnalysis.map((r, i) => (
              <li key={i}>
                <strong>{asText(r.risk)}</strong> — {asText(r.mitigation)}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {s.optimizationChecklist && (
        <Section title="Optimization Checklist">
          <List items={s.optimizationChecklist} />
        </Section>
      )}

      {Array.isArray(s.roadmap90Day) && s.roadmap90Day.length > 0 && (
        <Section title="90-Day Roadmap">
          {s.roadmap90Day.map((phase, i) => (
            <div key={i} className="strat-phase">
              <strong>{asText(phase.phase)}</strong>{" "}
              {phase.period && <span className="proj-muted">({asText(phase.period)})</span>}
              <List items={phase.actions} />
            </div>
          ))}
        </Section>
      )}

      {s.nextActions && (
        <Section title="Next Actions">
          <List items={s.nextActions} />
        </Section>
      )}
    </div>
  );
}
